import React, { useEffect, useState } from "react";
import { Box, Skeleton, Table, TableBody, TableCell, TableContainer, TableRow, Typography } from "@mui/material";
import PropTypes from "prop-types";

const YearOverYearSalesDetailPanel = ({ distributorId, sku }) => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);

    useEffect(() => {
        if (!distributorId || !sku) return;
        const controller = new AbortController();

        const fetchData = async () => {
            setLoading(true);
            setData(null);
            try {
                const response = await fetch(
                    `/forecastreview/weeklysalesreport?distributorId=${distributorId}&sku=${encodeURIComponent(sku)}`,
                    { signal: controller.signal }
                );
                if (response.ok) setData(await response.json());
            } catch (err) {
                if (err.name !== "AbortError") console.error("Historical Sales fetch error:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
        return () => controller.abort();
    }, [distributorId, sku]);

    const formatChange = (pct) => {
        if (pct == null) return "—";
        return `${pct > 0 ? "+" : ""}${pct.toFixed(1)}%`;
    };

    const changeColor = (pct) => {
        if (pct == null) return "inherit";
        return pct > 0 ? "green" : pct < 0 ? "red" : "inherit";
    };

    const periods = data?.periods ?? [];

    const monthlyAccuracy = data?.monthlyForecastAccuracy ?? null;

    const rows = [
        { label: "",             cells: periods.map((p) => ({ value: p.label, sx: { fontWeight: "bold" } })) },
        { label: "Current Year", cells: periods.map((p) => ({ value: p.currentYearSales ?? "—" })) },
        { label: "Prior Year",   cells: periods.map((p) => ({ value: p.priorYearSales ?? "—" })) },
        { label: "% Change",     cells: periods.map((p) => ({ value: formatChange(p.percentageChange), sx: { color: changeColor(p.percentageChange) } })) },
    ];

    return (
        <Box sx={{ px: 2, py: 1, backgroundColor: "#fafafa", borderTop: "1px solid #e0e0e0" }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, color: "text.secondary" }}>
                Historical Sales
            </Typography>

            {loading ? (
                <>
                    <Skeleton variant="rectangular" height={20} sx={{ mb: 0.5 }} />
                    <Skeleton variant="rectangular" height={90} />
                </>
            ) : periods.length > 0 ? (
                <TableContainer sx={{ width: "fit-content" }}>
                    <Table size="small" sx={{ width: "auto" }}>
                        <TableBody>
                            {rows.map((row, ri) => (
                                <TableRow key={ri} sx={{ "&:nth-of-type(odd)": { backgroundColor: "#f0f0f0" } }}>
                                    <TableCell sx={{ fontWeight: "bold", whiteSpace: "nowrap", py: 0.5 }}>
                                        {row.label}
                                    </TableCell>
                                    {row.cells.map((cell, ci) => (
                                        <TableCell key={ci} align="center" sx={{ py: 0.5, ...cell.sx }}>
                                            {cell.value}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                            <TableRow>
                                <TableCell sx={{ fontWeight: "bold", whiteSpace: "nowrap", py: 0.5 }}>
                                    Fcst Accuracy
                                </TableCell>
                                <TableCell
                                    colSpan={periods.length}
                                    align="center"
                                    sx={{
                                        py: 0.5,
                                        color: monthlyAccuracy == null
                                            ? "inherit"
                                            : monthlyAccuracy >= 95
                                                ? "green"
                                                : "orange",
                                    }}
                                >
                                    {monthlyAccuracy != null ? `${monthlyAccuracy.toFixed(1)}%` : "—"}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Typography variant="body2" color="text.secondary">
                    No sales data available for this period.
                </Typography>
            )}
        </Box>
    );
};

YearOverYearSalesDetailPanel.propTypes = {
    distributorId: PropTypes.number,
    sku: PropTypes.string,
};

export default YearOverYearSalesDetailPanel;
